import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Check, AlertTriangle } from "lucide-react";

enum PopularPlanType {
  NO = 0,
  YES = 1,
}

interface PricingProps {
  id: number; // Unique identifier for the plan
  title: string;
  popular: PopularPlanType;
  price: number;
  description: string;
  buttonText: string;
  benefitList: string[];
}

const pricingList: PricingProps[] = [
  {
    id: 1,
    title: "Free",
    popular: 0,
    price: 0,
    description:
      "Start your journey with charting, hotkey-enabled order management, multi-exchange displays, and so much more.",
    buttonText: "Begin Your Journey",
    benefitList: [
      "Account & Order Management",
      "Advanced Depth of Book",
      "Real-time Charting, Trading and Data",
      "Indicator Scripting",
      "Windows x64 Support",
    ],
  },
  {
    id: 2,
    title: "Premium",
    popular: 1,
    price: 29.99,
    description:
      "Elevate your trading with algorithmic trading, an AI-powered news feed, multi-charting, and so much more in the pipeline.",
    buttonText: "Achieve Mastery",
    benefitList: [
      "Everything in Free",
      "Exclusive Features",
      "Multiple Charts",
      "Algorithm Scripting",
      "AI-Powered Live News Feed",
      "Phone & Email Support"
    ],
  },
  {
    id: 3,
    title: "Enterprise",
    popular: 1,
    price: 0,
    description:
      "For institutions and teams requiring tailored solutions, dedicated support, and bulk licensing options.",
    buttonText: "Contact Us",
    benefitList: [
      "Premium Licenses for Teams",
      "Custom Integrations",
      "Priority Support",
      "Service Level Agreement",
      "Bulk Licensing Option",
      "On-prem Deployment Options",
      "White-glove Onboarding and Training",
    ],
  },
];

export const Pricing = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();  const handlePlanSelection = (pricing: PricingProps) => {
    if (pricing.title === "Enterprise") {
      // For enterprise, redirect to contact form
      navigate("/contact");
      return;
    }
    console.log("Selected plan:", pricing);
    console.log("User info:", user);
    if(user && isAuthenticated) {
      navigate("/checkout", {
        state: {
          selectedPlan: {
            id: pricing.id,
            title: pricing.title,
            price: pricing.price,
            description: pricing.description,
            benefitList: pricing.benefitList,
          },
          userInfo: user
        }
      });
    }
    else {
      // For Free and Premium, redirect to registration with plan information
      navigate("/register", {
        state: {
          selectedPlan: {
            id: pricing.id,
            title: pricing.title,
            price: pricing.price,
            description: pricing.description,
            benefitList: pricing.benefitList,
          }
        }
      });
    }
  };

  return (
    <div>
      <span className="text-sm">
        <img src="/icon.png" alt="Launch Icon" className="w-16 h-16 inline-block mr-2" /><strong>Launch Pricing:</strong> Get early access to our platform with significant savings.<br/>
        </span>
      <span className="text-sm dark:text-yellow-400">
        Each purchase includes one year of updates and support. 
      </span>
      <span className="text-sm"> Continue using your current version indefinitely after the license period.</span>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-4">
        {pricingList.map((pricing: PricingProps) => (
          <Card
            key={pricing.title}
            className={
              pricing.popular === PopularPlanType.YES
                ? "drop-shadow-xl shadow-black/10 dark:shadow-white/10"
                : ""
            }
          >
            <CardHeader>
              <CardTitle className="flex item-center justify-between">
                {pricing.title}
                {pricing.popular === PopularPlanType.YES ? (
                  <Badge
                    variant="secondary"
                    className="text-sm text-primary"
                  >
                    Phone & Email Support
                  </Badge>
                ) : null}
              </CardTitle>
              <div>
                {pricing.title === "Enterprise" ? (
                  <span className="text-3xl font-bold">Contact Us</span>
                ) : pricing.title === "Free" ? (
                  <>
                    <span className="text-3xl font-bold">$0</span>
                    <span className="text-muted-foreground"> forever</span>
                  </>
                ) : (
                  <>
                    <span className="text-3xl font-bold">${pricing.price}</span>
                    <span className="text-muted-foreground"> USD</span>
                  </>
                )}
              </div>
              <CardDescription>{pricing.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                className="w-full"
                onClick={() => handlePlanSelection(pricing)}
              >
                {pricing.buttonText}
              </Button>
            </CardContent>
            <hr className="w-4/5 m-auto mb-4" />
            <CardFooter className="flex">
              <div className="space-y-4">
                {pricing.benefitList.map((benefit: string) => (
                  <span
                    key={benefit}
                    className="flex"
                  >
                    <Check className="text-green-500" />{" "}
                    <h3 className="ml-2">{benefit}</h3>
                  </span>
                ))}
              </div>
            </CardFooter>
          </Card>
        ))}
      </div>

      <div className="mt-4">
        <div className="flex items-start gap-3 dark:bg-background text-primary">
          <AlertTriangle className="h-5 w-5 text-yellow-200 mt-0.5 flex-shrink-0" />
          <p className="text-sm">
            <strong>Platforms:</strong> Doyen and the Doyen Manager currently support <strong>64-bit Windows</strong> — 64-bit Linux and 32-bit production versions are coming soon. The Drasil algorithm server supports 64-bit Linux and Windows. Need 32‑bit now or another platform? Message us on <a href="https://discord.gg/ZCg7PMpsSe" target="_blank" rel="noopener noreferrer" className="text-primary underline">Discord</a> or email <a href="mailto:contact@doyentrading.com" className="text-primary underline">contact@doyentrading.com</a> and we'll send an experimental build and add your platform to our roadmap.
          </p>
        </div>
      </div>
    </div>
  );
};
